import React, { useState, useEffect, useCallback } from 'react';
import { Box, TableBody, InputAdornment, Typography, CircularProgress, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import CustomTableHeader from '../../../components/CustomHeader';
import CustomPagination from '../../../components/CustomPagination';
import ContactDetailModal from './ContactDetailModal';
import apiCall from '../../../utils/api';

import {
  PageWrapper,
  MainContentContainer,
  ContentHeader,
  SectionTitle,
  SearchField,
  TableWrapper,
  ContactTable,
  ContactTableRow,
  ContactTableCell,
  InfoItem,
  InfoLabel,
  InfoValue,
  MessageContainer
} from './styles';

const ContactRequestsPage: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('created_on');
  const [contacts, setContacts] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rowsPerPage = 10;

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(1);
  };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const ordering = order === 'desc' ? `-${orderBy}` : orderBy;
      const url = `/api/superadmin/contact/?page=${page}&search=${searchTerm}&ordering=${ordering}`;
      const response = await apiCall<any>('get', url);
      if (response.data && response.data.data) {
        setContacts(response.data.data.results || []);
        setTotalCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching contact requests:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, order, orderBy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchContacts]);

  useEffect(() => {
    setActivePage('contact-requests');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Contact Requests', path: '/super-admin/contact-requests' },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow]);

  const handleRowClick = (contact: any) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const CONTACT_COLUMNS = [
    { id: 'first_name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'organization_name', label: 'Organization' },
    { id: 'created_on', label: 'Date' },
  ];

  return (
    <PageWrapper>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#333' }}>
        Contact Requests
      </Typography>

      <MainContentContainer>
        <ContentHeader>
          <SectionTitle>All Requests</SectionTitle>
          <SearchField
            placeholder="Search by name or email"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999', fontSize: '18px' }} />
                </InputAdornment>
              ),
            }}
          />
        </ContentHeader>

        <TableWrapper>
          <ContactTable>
            <CustomTableHeader
              columns={CONTACT_COLUMNS}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {loading ? (
                <ContactTableRow>
                  <ContactTableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: '#5E8E7E' }} />
                  </ContactTableCell>
                </ContactTableRow>
              ) : contacts.length === 0 ? (
                <ContactTableRow>
                  <ContactTableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    No contact requests found
                  </ContactTableCell>
                </ContactTableRow>
              ) : contacts.map((contact) => (
                <ContactTableRow 
                  key={contact.contact_id}
                  onClick={() => handleRowClick(contact)}
                >
                  <ContactTableCell sx={{ fontWeight: 500 }}>
                    {contact.first_name} {contact.last_name}
                  </ContactTableCell>
                  <ContactTableCell>{contact.email}</ContactTableCell>
                  <ContactTableCell>{contact.organization_name || '-'}</ContactTableCell>
                  <ContactTableCell>{dayjs(contact.created_on).format('YYYY-MM-DD HH:mm')}</ContactTableCell>
                </ContactTableRow>
              ))}
            </TableBody>
          </ContactTable>
        </TableWrapper>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {contacts.length} of {totalCount} results
          </Typography>
          <CustomPagination
            totalPages={Math.ceil(totalCount / rowsPerPage)}
            currentPage={page}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>
      </MainContentContainer>

      <ContactDetailModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contact={selectedContact}
      >
        {selectedContact && (
          <Box sx={{ p: 3 }}>
            <InfoItem>
              <InfoLabel>Full Name</InfoLabel>
              <InfoValue>{selectedContact.first_name} {selectedContact.last_name}</InfoValue>
            </InfoItem>
            
            <InfoItem>
              <InfoLabel>Email Address</InfoLabel>
              <InfoValue>{selectedContact.email}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Phone Number</InfoLabel>
              <InfoValue>{selectedContact.phone}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Organization</InfoLabel>
              <InfoValue>{selectedContact.organization_name || 'Individual / Not Specified'}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Date Received</InfoLabel>
              <InfoValue>{dayjs(selectedContact.created_on).format('MMMM D, YYYY [at] h:mm A')}</InfoValue>
            </InfoItem>

            <Divider sx={{ my: 3 }} />

            <InfoItem>
              <InfoLabel>Message</InfoLabel>
              <MessageContainer>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#444', lineHeight: 1.6 }}>
                  {selectedContact.message}
                </Typography>
              </MessageContainer>
            </InfoItem>
          </Box>
        )}
      </ContactDetailModal>
    </PageWrapper>
  );
};

export default ContactRequestsPage;
